/* eslint-disable */
// @ts-nocheck

import { ChevronDown, RefreshCw } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Column } from '@tanstack/react-table'

import { IDropdownTableHeader } from '@/components/new/table/header/components/DropdownTableHeader'
import { HeaderElement } from '@/components/new/table/header/components/HeaderElement'
import { ITextTableHeader } from '@/components/new/table/header/components/TextTableHeader'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'

import { useTableContext } from '../TableContext'
import { IButtonTableHeader } from './components/ButtonTableHeader'

interface CommonHeaderButtonProps {
  label: string
  icon?: React.ReactNode
  type: string
}
export type HeaderActionButton = (
  | IButtonTableHeader
  | IDropdownTableHeader
  | ITextTableHeader
) &
  CommonHeaderButtonProps

interface HeaderActionsProps<TData, TValue> {
  columns: Column<TData, TValue>[]
}

export const HeaderActions = <TData, TValue>({
  columns,
}: HeaderActionsProps<TData, TValue>) => {
  const [isMobileView, setIsMobileView] = useState(false)

  // Use the context
  const {
    paginate,
    data,
    isLoading,
    refetch,
    rightButtons = [],
    leftButtons = [],
    showColumnsDropdown,
  } = useTableContext()

  // Check for mobile view on mount and window resize
  useEffect(() => {
    const checkViewport = () => {
      setIsMobileView(window.innerWidth < 768)
    }

    checkViewport()
    window.addEventListener('resize', checkViewport)

    return () => {
      window.removeEventListener('resize', checkViewport)
    }
  }, [])

  // For mobile: stacked layout
  if (isMobileView) {
    return (
      <div className="flex-col gap-4 p-4">
        <div className="mb-4 flex w-full items-center justify-between">
          {refetch && (
            <div className="flex items-center gap-2">
              <Button
                aria-label="Refrescar tabla"
                className="h-8 w-8"
                disabled={isLoading}
                onClick={refetch}
                size="icon"
                variant="ghost"
              >
                {isLoading ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </Button>
            </div>
          )}

          <div className="flex items-center gap-2">
            {showColumnsDropdown && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    aria-label="Mostrar columnas"
                    className="h-8 gap-1"
                    size="sm"
                    variant="outline"
                  >
                    <span>Columnas</span>
                    <ChevronDown className="h-3.5 w-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuLabel>Mostrar columnas</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {columns
                    .filter((column) => column.getCanHide())
                    .map((column) => {
                      return (
                        <DropdownMenuCheckboxItem
                          checked={column.getIsVisible()}
                          className="cursor-pointer"
                          key={column.id}
                          onCheckedChange={(value) =>
                            column.toggleVisibility(value)
                          }
                        >
                          {typeof column.columnDef.header === 'string'
                            ? column.columnDef.header
                            : column.id}
                        </DropdownMenuCheckboxItem>
                      )
                    })}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {rightButtons.map((btn, idx) => {
              return <HeaderElement headerActionButton={btn} key={idx} />
            })}
          </div>
        </div>

        <div className="flex w-full flex-col gap-2">
          {leftButtons.map((btn, idx) => {
            return <HeaderElement headerActionButton={btn} key={idx} />
          })}
        </div>
      </div>
    )
  }

  // For desktop: layout with creation buttons on left, pagination in middle, and utility buttons on right
  return (
    <div className="flex items-center justify-between p-4">
      {/* Left - Invoice creation buttons */}
      <div className="flex items-center gap-2">
        {leftButtons.map((btn, idx) => {
          return <HeaderElement headerActionButton={btn} key={idx} />
        })}
      </div>

      {/* Middle - Pagination info */}
      <div className="flex items-center gap-2">
        {refetch && (
          <Button
            aria-label="Refrescar tabla"
            className="h-8 w-8"
            disabled={isLoading}
            onClick={refetch}
            size="icon"
            variant="ghost"
          >
            {isLoading ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </Button>
        )}
        {paginate && (
          <span className="min-h-[20px] min-w-[300px] text-sm text-muted-foreground">
            {isLoading ? (
              <Skeleton className="h-4 w-[300px]" />
            ) : (
              <>
                Mostrando {(paginate.currentPage - 1) * paginate.pageSize + 1}{' '}
                al{' '}
                {Math.min(
                  paginate.currentPage * paginate.pageSize,
                  paginate.totalItems
                )}{' '}
                de {paginate.totalItems} resultados
              </>
            )}
          </span>
        )}
      </div>

      {/* Right - Export and columns buttons */}
      <div className="flex items-center gap-2">
        {rightButtons.map((btn, idx) => {
          return <HeaderElement headerActionButton={btn} key={idx} />
        })}
        {showColumnsDropdown && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                aria-label="Mostrar columnas"
                className="h-8 gap-1"
                size="sm"
                variant="outline"
              >
                <span>Columnas</span>
                <ChevronDown className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>Mostrar columnas</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {columns
                .filter((column) => column.getCanHide())
                .map((column) => {
                  return (
                    <DropdownMenuCheckboxItem
                      checked={column.getIsVisible()}
                      className="cursor-pointer"
                      key={column.id}
                      onCheckedChange={(value) =>
                        column.toggleVisibility(value)
                      }
                    >
                      {typeof column.columnDef.header === 'string'
                        ? column.columnDef.header
                        : column.id}
                    </DropdownMenuCheckboxItem>
                  )
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  )
}
